import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Chart as ChartJS } from 'chart.js';
import axiosInstance from '../../axiosConfig';
import './StrategyHealth.css';
import { useTheme } from '@mui/material/styles';
import GlassPaper from '../GlassPaper';

ChartJS.register(zoomPlugin);

const SERIES_COLORS = {
    Live: {
        base: 'rgba(34, 197, 94, 1)',
        negative: 'rgba(239, 68, 68, 1)',
    },
    Simulation: {
        base: 'rgba(59, 130, 246, 1)',
        negative: 'rgba(239, 68, 68, 1)',
    },
};

const CUMULATIVE_SERIES = {
    Live: {
        label: 'Live (Cumulative)',
        color: 'rgba(17, 24, 39, 0.9)',
    },
    Simulation: {
        label: 'Simulation (Cumulative)',
        color: 'rgba(30, 64, 175, 0.9)',
    },
};

const STRATEGIES = [
    { key: 'dhruva', title: 'dhruva', subtitle: 'Live vs Simulation PnL (net ₹, all accounts)' },
    { key: 'banknifty8', title: 'banknifty8', subtitle: 'Live vs Simulation PnL' },
    { key: 'cobra4', title: 'cobra4', subtitle: 'Live vs Simulation PnL' },
    { key: 'pelican', title: 'pelican', subtitle: 'Live vs Simulation PnL' },
    { key: 'cobra4_1', title: 'cobra4_1', subtitle: 'Live vs Simulation PnL' },
    { key: 'banknifty5', title: 'banknifty5', subtitle: 'Live vs Simulation PnL' },
    { key: 'banknifty1', title: 'banknifty1', subtitle: 'Live vs Simulation PnL' },
];

// Smart value label plugin — only renders labels that don't overlap
const valueLabelPlugin = {
    id: 'strategyHealthValueLabels',
    afterDatasetsDraw: (chart) => {
        const { ctx } = chart;
        if (!chart?.data?.datasets) return;

        ctx.save();
        ctx.font = '10px sans-serif';

        // Collect all potential labels with their bounding boxes
        const labels = [];
        chart.data.datasets.forEach((dataset, datasetIndex) => {
            if (typeof chart.isDatasetVisible === 'function' && !chart.isDatasetVisible(datasetIndex)) return;
            const meta = chart.getDatasetMeta(datasetIndex);
            if (!meta || meta.hidden || !meta.data) return;

            meta.data.forEach((element, index) => {
                if (!element || typeof element.tooltipPosition !== 'function') return;
                const tooltipPosition = element.tooltipPosition();
                if (!tooltipPosition) return;

                const rawPoint = Array.isArray(dataset.data) ? dataset.data[index] : null;
                const value = rawPoint && typeof rawPoint === 'object' ? rawPoint.y : rawPoint;
                if (typeof value !== 'number' || Number.isNaN(value)) return;

                const isNegative = value < 0;
                const formattedValue = value.toLocaleString('en-IN', {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                });
                const text = `₹${formattedValue}`;
                const textWidth = ctx.measureText(text).width;
                const verticalOffset = isNegative ? 12 : -12;
                const x = tooltipPosition.x;
                const y = tooltipPosition.y + verticalOffset;

                let fillStyle = dataset.borderColor || '#111827';
                if (dataset.label === 'Live') {
                    fillStyle = isNegative ? SERIES_COLORS.Live.negative : SERIES_COLORS.Live.base;
                } else if (dataset.label === 'Simulation') {
                    fillStyle = isNegative ? SERIES_COLORS.Simulation.negative : SERIES_COLORS.Simulation.base;
                } else {
                    const cumulativeMatch = Object.values(CUMULATIVE_SERIES).find((s) => s.label === dataset.label);
                    if (cumulativeMatch) fillStyle = cumulativeMatch.color;
                    else if (Array.isArray(dataset.borderColor)) fillStyle = dataset.borderColor[0];
                }

                // Priority: cumulative lines get lower priority, daily PnL gets higher
                const isCumulative = dataset.label?.includes('Cumulative');
                const priority = isCumulative ? 0 : 1;

                labels.push({
                    text, x, y, fillStyle, priority,
                    bbox: { left: x - textWidth / 2 - 2, right: x + textWidth / 2 + 2, top: y - 6, bottom: y + 6 },
                });
            });
        });

        // Sort by priority (higher first), then render only non-overlapping labels
        labels.sort((a, b) => b.priority - a.priority);
        const placed = [];

        for (const label of labels) {
            const overlaps = placed.some(
                (p) =>
                    label.bbox.left < p.bbox.right &&
                    label.bbox.right > p.bbox.left &&
                    label.bbox.top < p.bbox.bottom &&
                    label.bbox.bottom > p.bbox.top,
            );
            if (!overlaps) {
                placed.push(label);
                ctx.fillStyle = label.fillStyle;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(label.text, label.x, label.y);
            }
        }

        ctx.restore();
    },
};

const chartAreaBackgroundPlugin = {
    id: 'strategyHealthChartAreaBackground',
    beforeDraw: (chart, _args, pluginOptions) => {
        const { ctx, chartArea } = chart;
        if (!ctx || !chartArea) return;
        const { left, top, width, height } = chartArea;
        ctx.save();
        ctx.fillStyle = pluginOptions?.color || 'transparent';
        ctx.fillRect(left, top, width, height);
        ctx.restore();
    },
};

// Minimap component — shows full data range with a viewport indicator
const Minimap = ({ chartData, viewRange, fullRange, onRangeClick, isDark }) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !chartData?.datasets?.length) return;

        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.parentElement.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = 50 * dpr;
        canvas.style.width = `${rect.width}px`;
        canvas.style.height = '50px';
        ctx.scale(dpr, dpr);

        const w = rect.width;
        const h = 50;
        const padding = { left: 4, right: 4, top: 8, bottom: 14 };
        const plotW = w - padding.left - padding.right;
        const plotH = h - padding.top - padding.bottom;

        // Clear
        ctx.clearRect(0, 0, w, h);

        // Find full data extents
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        chartData.datasets.forEach((ds) => {
            ds.data.forEach((p) => {
                const t = p.x.getTime();
                if (t < minX) minX = t;
                if (t > maxX) maxX = t;
                if (p.y < minY) minY = p.y;
                if (p.y > maxY) maxY = p.y;
            });
        });
        if (minX === Infinity) return;

        const xSpan = maxX - minX || 1;
        const ySpan = maxY - minY || 1;
        const toCanvasX = (t) => padding.left + ((t - minX) / xSpan) * plotW;
        const toCanvasY = (v) => padding.top + plotH - ((v - minY) / ySpan) * plotH;

        // Draw lines for each dataset
        chartData.datasets.forEach((ds) => {
            if (ds.data.length < 2) return;
            ctx.beginPath();
            ctx.strokeStyle = typeof ds.borderColor === 'string' ? ds.borderColor : (isDark ? 'rgba(148,163,184,0.5)' : 'rgba(100,116,139,0.5)');
            ctx.lineWidth = 1;
            if (ds.borderDash) ctx.setLineDash(ds.borderDash);
            else ctx.setLineDash([]);

            ds.data.forEach((p, i) => {
                const cx = toCanvasX(p.x.getTime());
                const cy = toCanvasY(p.y);
                if (i === 0) ctx.moveTo(cx, cy);
                else ctx.lineTo(cx, cy);
            });
            ctx.stroke();
        });
        ctx.setLineDash([]);

        // Draw viewport highlight
        if (viewRange && fullRange) {
            const vLeft = toCanvasX(viewRange.min);
            const vRight = toCanvasX(viewRange.max);

            // Dim outside viewport
            ctx.fillStyle = isDark ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.12)';
            ctx.fillRect(padding.left, padding.top, vLeft - padding.left, plotH);
            ctx.fillRect(vRight, padding.top, w - padding.right - vRight, plotH);

            // Viewport border
            ctx.strokeStyle = isDark ? 'rgba(99,179,237,0.8)' : 'rgba(59,130,246,0.7)';
            ctx.lineWidth = 1.5;
            ctx.strokeRect(vLeft, padding.top, vRight - vLeft, plotH);

            // Viewport fill
            ctx.fillStyle = isDark ? 'rgba(99,179,237,0.08)' : 'rgba(59,130,246,0.06)';
            ctx.fillRect(vLeft, padding.top, vRight - vLeft, plotH);
        }

        // Zero line
        if (minY < 0 && maxY > 0) {
            const zeroY = toCanvasY(0);
            ctx.strokeStyle = isDark ? 'rgba(148,163,184,0.3)' : 'rgba(107,114,128,0.25)';
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(padding.left, zeroY);
            ctx.lineTo(w - padding.right, zeroY);
            ctx.stroke();
        }

    }, [chartData, viewRange, fullRange, isDark]);

    const handleClick = useCallback((e) => {
        const canvas = canvasRef.current;
        if (!canvas || !chartData?.datasets?.length) return;

        const rect = canvas.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const w = rect.width;
        const padding = { left: 4, right: 4 };
        const plotW = w - padding.left - padding.right;

        let minX = Infinity, maxX = -Infinity;
        chartData.datasets.forEach((ds) => {
            ds.data.forEach((p) => {
                const t = p.x.getTime();
                if (t < minX) minX = t;
                if (t > maxX) maxX = t;
            });
        });
        if (minX === Infinity) return;

        const xSpan = maxX - minX || 1;
        const clickTime = minX + ((clickX - padding.left) / plotW) * xSpan;

        // Center a 14-day window on click point
        const windowMs = 14 * 24 * 60 * 60 * 1000;
        const newMin = Math.max(minX, clickTime - windowMs / 2);
        const newMax = Math.min(maxX, clickTime + windowMs / 2);

        onRangeClick({ min: newMin, max: newMax });
    }, [chartData, onRangeClick]);

    return (
        <div className="strategy-minimap" ref={containerRef}>
            <canvas ref={canvasRef} onClick={handleClick} style={{ cursor: 'pointer', width: '100%' }} />
            <span className="strategy-minimap__label">Overview — click to navigate, drag on chart to zoom</span>
        </div>
    );
};

const formatDateRange = (min, max) => {
    if (!min || !max) return '';
    const fmt = (d) => new Date(d).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${fmt(min)} — ${fmt(max)}`;
};

const StrategyCard = ({ strategyKey, title, subtitle }) => {
    const [pnlSeries, setPnlSeries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [isZoomed, setIsZoomed] = useState(false);
    const [viewRange, setViewRange] = useState(null);
    const [fullRange, setFullRange] = useState(null);
    const chartRef = useRef(null);
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const handleZoomComplete = useCallback(({ chart }) => {
        const xScale = chart.scales.x;
        if (xScale) {
            setViewRange({ min: xScale.min, max: xScale.max });
            setIsZoomed(true);
        }
    }, []);

    const handleResetZoom = useCallback(() => {
        const chart = chartRef.current;
        if (chart) {
            chart.resetZoom();
            setIsZoomed(false);
            setViewRange(null);
        }
    }, []);

    const handleMinimapClick = useCallback(({ min, max }) => {
        const chart = chartRef.current;
        if (!chart) return;

        // Zoom to specific range
        chart.zoomScale('x', { min, max }, 'default');
        setViewRange({ min, max });
        setIsZoomed(true);
    }, []);

    const chartOptions = useMemo(() => {
        const axisColor = isDark ? 'rgba(226, 232, 240, 0.92)' : '#0f172a';
        const gridColor = isDark ? 'rgba(148, 163, 184, 0.28)' : 'rgba(209, 213, 219, 0.25)';
        const zeroLineColor = isDark ? 'rgba(226, 232, 240, 0.65)' : '#6b7280';
        const tooltipBackground = isDark ? 'rgba(15, 23, 42, 0.88)' : 'rgba(255, 255, 255, 0.92)';
        const tooltipBorder = isDark ? 'rgba(148, 163, 184, 0.35)' : 'rgba(59, 130, 246, 0.2)';
        const tooltipColor = isDark ? '#e2e8f0' : '#0f172a';
        const backgroundColor = isDark ? 'rgba(13, 24, 41, 0.75)' : 'rgba(255, 255, 255, 0.85)';
        const dragColor = isDark ? 'rgba(99, 179, 237, 0.25)' : 'rgba(59, 130, 246, 0.2)';
        const dragBorder = isDark ? 'rgba(99, 179, 237, 0.6)' : 'rgba(59, 130, 246, 0.5)';

        return {
            responsive: true,
            maintainAspectRatio: false,
            animation: {
                duration: 300,
            },
            plugins: {
                legend: {
                    display: true,
                    position: 'bottom',
                    labels: {
                        color: axisColor,
                        usePointStyle: true,
                    },
                },
                tooltip: {
                    backgroundColor: tooltipBackground,
                    borderColor: tooltipBorder,
                    borderWidth: 1,
                    titleColor: tooltipColor,
                    bodyColor: tooltipColor,
                    callbacks: {
                        label: (context) => {
                            const value = context.parsed?.y ?? 0;
                            const formatted = value.toLocaleString('en-IN', {
                                maximumFractionDigits: 2,
                                minimumFractionDigits: 2,
                            });
                            return `${context.dataset?.label || 'PnL'}: ₹${formatted}`;
                        },
                    },
                },
                zoom: {
                    zoom: {
                        drag: {
                            enabled: true,
                            backgroundColor: dragColor,
                            borderColor: dragBorder,
                            borderWidth: 1,
                        },
                        mode: 'x',
                        onZoomComplete: handleZoomComplete,
                    },
                    pan: {
                        enabled: true,
                        mode: 'x',
                        onPanComplete: handleZoomComplete,
                    },
                    limits: {
                        x: { minRange: 3 * 24 * 60 * 60 * 1000 }, // min 3 days visible
                    },
                },
                strategyHealthChartAreaBackground: {
                    color: backgroundColor,
                },
            },
            scales: {
                x: {
                    type: 'time',
                    time: {
                        tooltipFormat: 'MMM dd, yyyy',
                        unit: 'day',
                        displayFormats: {
                            day: 'MMM dd',
                        },
                    },
                    ticks: {
                        maxRotation: 0,
                        autoSkip: true,
                        color: axisColor,
                    },
                    grid: {
                        color: gridColor,
                    },
                    border: {
                        color: axisColor,
                    },
                },
                y: {
                    ticks: {
                        color: axisColor,
                        callback: (value) => {
                            if (typeof value !== 'number') return value;
                            return `₹${value.toLocaleString('en-IN')}`;
                        },
                    },
                    grid: {
                        color: (context) => (context.tick.value === 0 ? zeroLineColor : gridColor),
                        lineWidth: (context) => (context.tick.value === 0 ? 2 : 1),
                    },
                    border: {
                        color: axisColor,
                    },
                    title: {
                        display: true,
                        text: 'PnL',
                        color: axisColor,
                    },
                },
            },
        };
    }, [isDark, handleZoomComplete]);

    const fetchStrategyPnl = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/api/strategy-health/${strategyKey}/pnl`);
            setPnlSeries(Array.isArray(response.data) ? response.data : []);
            setLastUpdated(new Date());
            setError(null);
        } catch (err) {
            console.error(`Error fetching strategy PnL for ${strategyKey}:`, err);
            setError('Unable to load PnL data right now. Please try again later.');
        } finally {
            setLoading(false);
        }
    }, [strategyKey]);

    useEffect(() => {
        fetchStrategyPnl();
    }, [fetchStrategyPnl]);

    const chartData = useMemo(() => {
        const grouped = pnlSeries.reduce((acc, row) => {
            if (!row) return acc;
            const type = row.pnl_type || 'Unknown';
            const date = row.pnl_date ? new Date(row.pnl_date) : null;
            const value = Number(row.pnl);
            if (!date || Number.isNaN(value)) return acc;
            if (!acc[type]) acc[type] = [];
            acc[type].push({ x: date, y: value });
            return acc;
        }, {});

        const baseDatasets = Object.entries(grouped).map(([label, points]) => {
            points.sort((a, b) => a.x - b.x);
            const colors = SERIES_COLORS[label] || SERIES_COLORS.Live;

            return {
                label,
                data: points,
                borderWidth: 2,
                fill: false,
                spanGaps: true,
                tension: 0.3,
                pointRadius: 3,
                pointHoverRadius: 5,
                pointBackgroundColor: (context) => {
                    const value = context.parsed?.y;
                    return typeof value === 'number' && value < 0 ? colors.negative : colors.base;
                },
                borderColor: colors.base,
                segment: {
                    borderColor: (ctx) => {
                        const first = ctx.p0?.parsed?.y;
                        const second = ctx.p1?.parsed?.y;
                        if (typeof first !== 'number' || typeof second !== 'number') return colors.base;
                        return first < 0 && second < 0 ? colors.negative : colors.base;
                    },
                },
            };
        });

        const cumulativeDatasets = ['Live', 'Simulation'].reduce((acc, label) => {
            const baseDataset = baseDatasets.find((ds) => ds.label === label);
            const cumulativeMeta = CUMULATIVE_SERIES[label];
            if (!baseDataset || !cumulativeMeta) return acc;

            let runningTotal = 0;
            const cumulativePoints = baseDataset.data.map((point) => {
                runningTotal += point.y;
                return { x: point.x, y: runningTotal };
            });

            acc.push({
                label: cumulativeMeta.label,
                data: cumulativePoints,
                borderWidth: 2,
                borderColor: cumulativeMeta.color,
                fill: false,
                spanGaps: true,
                tension: 0.25,
                borderDash: [6, 4],
                pointRadius: 0,
                pointHoverRadius: 4,
            });
            return acc;
        }, []);

        return { datasets: [...baseDatasets, ...cumulativeDatasets] };
    }, [pnlSeries]);

    // Compute full date range for minimap
    useEffect(() => {
        if (!chartData?.datasets?.length) return;
        let min = Infinity, max = -Infinity;
        chartData.datasets.forEach((ds) => {
            ds.data.forEach((p) => {
                const t = p.x.getTime();
                if (t < min) min = t;
                if (t > max) max = t;
            });
        });
        if (min !== Infinity) setFullRange({ min, max });
    }, [chartData]);

    const hasChartData = chartData.datasets?.some((ds) => Array.isArray(ds.data) && ds.data.length > 0);

    // Keyboard shortcut: Escape to reset zoom
    useEffect(() => {
        const handler = (e) => {
            if (e.key === 'Escape' && isZoomed) handleResetZoom();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [isZoomed, handleResetZoom]);

    return (
        <GlassPaper className="strategy-card" elevation={0} square={false} component="section">
            <div className="strategy-card__header">
                <div className="strategy-card__title">
                    <h3>{title}</h3>
                    {subtitle && <span className="strategy-card__subtitle">{subtitle}</span>}
                </div>
                <div className="strategy-card__actions">
                    {lastUpdated && (
                        <span className="strategy-card__timestamp">UPDATED {lastUpdated.toLocaleString()}</span>
                    )}
                    <button className="btn btn-sm btn-outline-secondary" onClick={fetchStrategyPnl} disabled={loading}>
                        {loading ? 'Loading…' : 'Refresh'}
                    </button>
                </div>
            </div>

            {error && <div className="strategy-card__error">{error}</div>}

            {/* Zoom controls bar */}
            {hasChartData && !loading && (
                <div className={`strategy-card__zoom-bar ${isZoomed ? 'strategy-card__zoom-bar--active' : ''}`}>
                    {isZoomed ? (
                        <>
                            <span className="strategy-card__zoom-range">
                                {viewRange ? formatDateRange(viewRange.min, viewRange.max) : 'Zoomed'}
                            </span>
                            <div className="strategy-card__zoom-actions">
                                <button className="strategy-card__zoom-reset" onClick={handleResetZoom}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="1 4 1 10 7 10" />
                                        <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                                    </svg>
                                    Reset Zoom
                                </button>
                                <kbd className="strategy-card__zoom-hint">ESC</kbd>
                            </div>
                        </>
                    ) : (
                        <span className="strategy-card__zoom-tip">Drag on chart to zoom into a region</span>
                    )}
                </div>
            )}

            <div className="strategy-card__chart">
                {loading ? (
                    <div className="strategy-card__loading">Loading PnL…</div>
                ) : !hasChartData ? (
                    <div className="strategy-card__loading">No PnL records available for this strategy.</div>
                ) : (
                    <Line
                        ref={chartRef}
                        data={chartData}
                        options={chartOptions}
                        plugins={[valueLabelPlugin, chartAreaBackgroundPlugin]}
                    />
                )}
            </div>

            {/* Minimap */}
            {hasChartData && !loading && (
                <Minimap
                    chartData={chartData}
                    viewRange={viewRange}
                    fullRange={fullRange}
                    onRangeClick={handleMinimapClick}
                    isDark={isDark}
                />
            )}
        </GlassPaper>
    );
};

const StrategyHealth = () => (
    <div className="strategy-health">
        <h2 className="dashboard-title">Strategy Health</h2>
        {STRATEGIES.map(({ key, title, subtitle }) => (
            <StrategyCard key={key} strategyKey={key} title={title} subtitle={subtitle} />
        ))}
    </div>
);

export default StrategyHealth;
